import React from "react";
import styled from "styled-components";
import PositionedWindow from "../PositionedWindow";
import Taskbar, { TASKBAR_HEIGHT } from "../Taskbar";
import StartMenu from "../StartMenu";
import DesktopIcon from "../DesktopIcon";
import HistoryWindow from "../HistoryWindow";
import PastGameWindow from "../PastGameWindow";
import AboutWindow from "../AboutWindow";
import StatsWindow from "../StatsWindow";
import LoginDialog from "../LoginDialog";
import GhostCursor from "../GhostCursor";
import { RecycleBinIcon, RecycleBinWindow } from "../RecycleBin";
import DisplayProperties from "../DisplayProperties";
import BootScreen from "../BootScreen";
import SettingsWindow, { ALWAYS_BOOT_KEY } from "../SettingsWindow";
import ActiveGamesWindow from "../ActiveGamesWindow";
import PixelIcon from "../PixelIcon";
import useCompactMode, { useViewportSize } from "../../hooks/useCompactMode";
import { readSetting } from "../../hooks/useSetting";
import useKey from "../../hooks/useKey";
import { setModalGate } from "../../hooks/modalGate";
import SolitaireCore, { scaledWindowSize } from "../SolitaireCore";
import MessageBox from "../MessageBox";
import { GAME_WINDOW_WIDTH, GAME_WINDOW_HEIGHT } from "./useRoomLayout";
import { newGame, applyMoveByIndex, legalMoves } from "../../engine/solitaire";
import { clearStats } from "../../stats";
import { useKiosk } from "../../kiosk";
import {
  KIND,
  PRIORITY,
  initialState as wmInitial,
  reducer as wmReducer,
  getWindow,
  isOpen,
  isMinimized,
  activeModal,
  focusedId as wmFocusedId,
  zIndexOf as wmZIndexOf,
  taskbarWindows as wmTaskbarWindows,
} from "./windowManager";
import {
  unlock as unlockAudio,
  click as clickSound,
  startHum,
  powerOff as powerOffAudio,
} from "../../sound/sound";
// The period arrow cursor (same drawing as the ghost/remote cursors),
// rasterized by scripts/make-cursor.js. PNG because Safari doesn't
// support SVG cursors. Inlined as a data: URI (Parcel's data-url:
// pipeline) so the OS cursor never flashes before it loads; the same
// inline rule sits on html/body in global-styles.css for the pre-mount
// paint. Fetched asset URLs must use new URL(..., import.meta.url) — a
// bare `import x from "*.png"` compiles silently to an empty object.
import cursorArrowUrl from "data-url:../../cursor-arrow.png";

// The Stage is the desktop (see CLAUDE.md "Arrival"). Flow:
//   login  — Win95 logon dialog over the bare desktop; the OK click is
//            the user gesture that unlocks audio.
//   scene  — first visit only: the real cursor hides and a scripted
//            ghost cursor opens Start → Programs → Solitaire Alone
//            Together. Any click skips it.
//   play   — your game window centered, About on top (first visit),
//            neighbors scattered behind at reduced scale, some hanging
//            off the edges. No camera, no zoom, ever.

// Every game window renders at 1:1 — a real desktop doesn't have
// shrunken windows, and fractional scaling resamples the pixel
// font/sprites into mush. The room reads through overlap and off-edge
// hang instead of size.
const EMPTY_ID_SET = new Set();
const EMPTY_STATE_MAP = new Map();
// Visible-neighbor cap is always slotsRef.current.length — one window
// per slot, never more — so recorded layouts (layouts.js) can seat
// rooms of any size without overflow windows colliding at a fallback slot.

const BOOT_SEEN_KEY = "saat.bootSeen";
// Re-show the full opening after a week away.
const BOOT_RESHOW_MS = 7 * 24 * 60 * 60 * 1000;
const SELF_ID = "self";

function readUiZoom() {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(
    "--ui-zoom",
  );
  const n = parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

// Five neighbor slots: 2 "top row-ish" with strong vertical variation,
// 3 "bottom row-ish" staggered rather than a straight row, so the
// desktop peeks through instead of reading as a grid. The upper-left
// corner stays clear for desktop icons. Windows may hang a little
// off-edge (OFF_EDGE), never mostly offscreen.
const SCATTER_ANCHORS = [
  // top pair (right of the icon column, y's far apart)
  { x: 0.06, y: -0.45 },
  { x: 0.36, y: -0.28 },
  // bottom trio (staggered, not a straight row)
  { x: -0.34, y: 0.34 },
  { x: 0.04, y: 0.5 },
  { x: 0.38, y: 0.2 },
];

function generateScatterSlots() {
  const zoom = readUiZoom();
  const W = window.innerWidth / zoom;
  const H = (window.innerHeight - TASKBAR_HEIGHT) / zoom;
  const jitter = (n) => (Math.random() * 2 - 1) * n;
  // Spawn clamp: at most OFF_EDGE px of a window may hang off any
  // side, and the title bar always stays fully reachable (top edge
  // on-screen, bar above the taskbar). Coordinates are window-center
  // offsets from the viewport center.
  const OFF_EDGE = 120;
  const maxX = Math.max(0, W / 2 - GAME_WINDOW_WIDTH / 2 + OFF_EDGE);
  const minY = 6 - H / 2 + GAME_WINDOW_HEIGHT / 2; // top edge on-screen
  const maxY = H / 2 - GAME_WINDOW_HEIGHT / 2 + OFF_EDGE;
  return SCATTER_ANCHORS.map((a) => {
    const x = Math.max(-maxX, Math.min(maxX, a.x * W + jitter(70)));
    const y = Math.max(minY, Math.min(Math.max(minY, maxY), a.y * H + jitter(90)));
    return { x, y };
  });
}

// Dev knob: `?fakechaos=40` fills All Active Games with N locally
// simulated boards so virtualization can be tested without a full room.
function readFakeChaos() {
  const raw = new URLSearchParams(window.location.search).get("fakechaos");
  const n = parseInt(raw ?? "", 10);
  return Number.isFinite(n) && n > 0 ? Math.min(n, 200) : 0;
}

function fakeChaosStates(count) {
  const states = [];
  for (let i = 0; i < count; i++) {
    let st = newGame(1000000 + i);
    let seed = (i + 1) * 2654435761;
    const rand = () => {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      return seed / 2 ** 32;
    };
    const moves = 10 + Math.floor(rand() * 40);
    for (let m = 0; m < moves; m++) {
      const legal = legalMoves(st);
      if (legal.length === 0) break;
      st = applyMoveByIndex(st, Math.floor(rand() * legal.length));
    }
    states.push(st);
  }
  return states;
}

function Stage({
  playerSlot,
  neighborSlots,
  browserGameSlots = [],
  playerGameId,
  // True once the server has timed the game out: the window is gone
  // (closed, no taskbar button); the system notice handles what's next.
  playerGameOver = false,
  // True once your own win has echoed back: "Deal New Game" has nothing
  // to abandon and skips the confirm.
  playerGameWon = false,
  // The one ambient notice App wants shown (already priority-selected:
  // timed-out > net-trouble > still-there), or null. Renders through
  // the same modal slot as every other dialog.
  systemNotice = null,
  dealNewGame,
  // Powering off gives the game up immediately (fire-and-forget wire
  // abandon) — you can't resume a computer you turned off.
  abandonGame,
  roomActive = 0,
  browseGameIds = [],
  browseEndedIds = EMPTY_ID_SET,
  browseReadyIds = EMPTY_ID_SET,
  browseFinalStates = EMPTY_STATE_MAP,
  openGameBrowser,
  setBrowsedGames,
  loadBrowseFinal,
  closeGameBrowser,
  watchGame,
  unwatchGame,
  // A watched window's departure ceremony finished — the hook hides it
  // until the server's reseat welcome turns the seat over.
  departGame,
  // Game ids whose windows must open silently — reconnect reseats of
  // seats that died offline. Everything else joining mid-play gets the
  // wireframe arrival.
  quietArrivalIds = EMPTY_ID_SET,
  roomLayout = null,
  onPhaseChange,
}) {
  const compact = useCompactMode();
  const { width: viewportWidth, height: viewportHeight } = useViewportSize();
  // `?boot` forces the full first-visit opening (POST, splash, scene)
  // without having to clear localStorage by hand.
  const forceBoot = React.useMemo(
    () => new URLSearchParams(window.location.search).has("boot"),
    [],
  );
  // `?noboot`: dev knob for automated tests — skips the scene/About and
  // shrinks the POST hold, so a fresh profile doesn't sit through the
  // whole scripted opening. The login click stays (it's the
  // audio-unlock gesture).
  const skipBoot = React.useMemo(
    () => new URLSearchParams(window.location.search).has("noboot"),
    [],
  );
  const firstVisit = React.useMemo(() => {
    if (skipBoot) return false;
    if (forceBoot) return true;
    // Settings → "always play the opening scene": desktop only — compact
    // has no scene, so the pref would just re-pop About every visit there.
    if (!compact && readSetting(ALWAYS_BOOT_KEY, false)) return true;
    try {
      // The stamp is the last time the opening finished (unparseable
      // values are treated as long-ago). Away a week or more re-shows it.
      const t = parseInt(localStorage.getItem(BOOT_SEEN_KEY), 10);
      if (!Number.isFinite(t)) return true;
      return Date.now() - t > BOOT_RESHOW_MS;
    } catch {
      return true; // storage denied — treat every visit as the first
    }
  }, [skipBoot, forceBoot, compact]);

  // post → login → scene (first visit, not compact) → play. POST plays
  // every visit — App already shows it while the socket connects, and
  // holding it a beat longer lets boot cover loading.
  const [phase, setPhase] = React.useState("post");

  React.useEffect(() => {
    if (phase === "post") {
      const t = setTimeout(() => setPhase("login"), skipBoot ? 50 : 1700);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [phase, skipBoot]);

  React.useEffect(() => {
    if (onPhaseChange) onPhaseChange(phase);
  }, [phase, onPhaseChange]);
  // Which beat of the scripted scene we're on (drives menu state).
  const [sceneBeat, setSceneBeat] = React.useState(0);
  // Act 2 of the scene: phase is already "play" (the desk is up, the
  // game window just booted) but the ghost cursor keeps the stage to
  // open About through the real Help menu.
  const [sceneAct2, setSceneAct2] = React.useState(false);

  // The window registry (windowManager.js): one pure reducer owns every
  // window's existence, stacking, focus, minimize state, and the single
  // modal slot. Everything the shell shows is derived from `wm` — no
  // parallel open/minimized/zOrder structures to drift.
  const [wm, wmDispatch] = React.useReducer(wmReducer, wmInitial);
  const focused = wmFocusedId(wm);
  const modalWin = activeModal(wm);

  // Transient popups (Start menu, context menus) share the manager's
  // one popup slot: at most one open, opening another replaces it.
  const popup = wm.popup;
  const openPopup = (p) => wmDispatch({ type: "openPopup", popup: p });
  const closePopup = React.useCallback(
    () => wmDispatch({ type: "closePopup" }),
    [],
  );
  const startOpen = popup?.id === "start";
  const setStartOpen = (v) =>
    v ? openPopup({ id: "start" }) : closePopup();
  const ctxMenu = popup?.id === "ctx" ? popup : null;
  const taskbarMenu = popup?.id === "tb" ? popup : null;

  // Dialogs are windows with modal: true; content travels in the
  // window payload and renders through the one MessageBox host below.
  const openDialog = (id, priority, payload) =>
    wmDispatch({
      type: "open",
      id,
      kind: KIND.DIALOG,
      modal: true,
      priority,
      payload,
    });
  const closeDialog = React.useCallback(
    (id) => wmDispatch({ type: "close", id }),
    [],
  );

  // Keyboard modality follows the modal slot: while a dialog is up,
  // every useKey outside it goes inert (see modalGate.js).
  React.useEffect(() => {
    setModalGate(modalWin != null);
    return () => setModalGate(false);
  }, [modalWin]);

  // All Active Games is an ordinary containing window now. Its room
  // roster may be large, but only the virtualized visible+overscan range
  // receives board state and live traffic.
  const chaosOpen = isOpen(wm, "chaos");
  const fakeChaosCount = React.useMemo(readFakeChaos, []);
  const fakeChaos = React.useMemo(
    () => (fakeChaosCount ? fakeChaosStates(fakeChaosCount) : []),
    [fakeChaosCount],
  );
  const fakeChaosIds = React.useMemo(
    () => fakeChaos.map((_, i) => 1000000 + i),
    [fakeChaos],
  );
  const fakeChaosSlots = React.useMemo(
    () =>
      fakeChaos.map((state, i) => (
        <SolitaireCore
          key={1000000 + i}
          title={`Game #${1000000 + i} — Solitaire Alone Together`}
          state={state}
          interactive={false}
          inactive
          disabledControls={["minimize", "maximize", "close"]}
        />
      )),
    [fakeChaos],
  );
  const fakeChaosIdSet = React.useMemo(
    () => new Set(fakeChaosIds),
    [fakeChaosIds],
  );
  const ignoreBrowseRange = React.useCallback(() => {}, []);
  const activeBrowserIds = fakeChaosCount ? fakeChaosIds : browseGameIds;
  const activeBrowserEndedIds = fakeChaosCount
    ? EMPTY_ID_SET
    : browseEndedIds;
  const activeBrowserReadyIds = fakeChaosCount
    ? fakeChaosIdSet
    : browseReadyIds;
  const updateBrowserRange = fakeChaosCount
    ? ignoreBrowseRange
    : setBrowsedGames ?? ignoreBrowseRange;
  // Larger than one 620x435 game when the viewport permits, capped so a
  // cinema display cannot turn the browser into another unbounded wall.
  // 0.78 makes it a little taller than the game's 0.70 height/width.
  const activeBrowserWidth = Math.min(
    1060,
    Math.max(660, viewportWidth - 80),
  );
  const activeBrowserHeight = Math.max(
    360,
    Math.min(
      820,
      Math.round(activeBrowserWidth * 0.78),
      viewportHeight - TASKBAR_HEIGHT - 48,
    ),
  );
  // Watching a game that already has a desktop window should reveal the
  // thing that's already there, not manufacture another arrival. A short
  // Win95-style active/inactive title-bar flash makes the raised window
  // findable in an overlapping room.
  const [attentionFlash, setAttentionFlash] = React.useState(null);
  React.useEffect(() => {
    if (!attentionFlash) return undefined;
    const t = setTimeout(() => {
      setAttentionFlash((prev) => {
        if (!prev || prev.id !== attentionFlash.id) return prev;
        return prev.toggles <= 1
          ? null
          : { ...prev, toggles: prev.toggles - 1 };
      });
    }, 80);
    return () => clearTimeout(t);
  }, [attentionFlash]);
  // Watch is a socket round trip. Compact mode opens the chosen window
  // optimistically; close it again if no authoritative welcome arrives.
  // Optimistic compact-mode watch: the window opens immediately and a
  // 5s timer closes it again if the watch never lands in the
  // authoritative set. Keyed timers ARE the pending state.
  const pendingWatchTimersRef = React.useRef(new Map());
  React.useEffect(
    () => () => {
      for (const timer of pendingWatchTimersRef.current.values()) {
        clearTimeout(timer);
      }
    },
    [],
  );
  // Win95-style confirmation before abandoning your own game — via the
  // titlebar X, the Game menu, or Start -> Deal New Game (all the same
  // semantic: walk away, fresh deal). Copy is verbatim canon.
  const requestDeal = dealNewGame
    ? () => {
        // A won game has nothing to abandon: deal straight away, no dialog.
        if (playerGameWon) {
          dealNewGame();
          return;
        }
        openDialog("dlg:deal", PRIORITY.CONFIRM, {
          title: "Solitaire Alone Together",
          icon: "question",
          message: "Deal a new game?",
          buttons: [
            {
              label: "OK",
              onClick: () => {
                closeDialog("dlg:deal");
                dealNewGame();
              },
            },
            { label: "Cancel", onClick: () => closeDialog("dlg:deal") },
          ],
          onCancel: () => closeDialog("dlg:deal"),
        });
      }
    : undefined;
  // Dragging the Solitaire icon into the Recycle Bin asks "are you
  // sure" and, on Yes, ends the visit; dragging the Past Games icon in
  // bins it for the session (double-click it in the bin to restore).
  const [binnedViewer, setBinnedViewer] = React.useState(false);
  // One past-game viewer window at a time: opening another entry swaps
  // the contents of the same window and re-foregrounds it —
  // Explorer-with-one-preview, not a window per file.
  const [pastGameId, setPastGameId] = React.useState(null);
  const openPastGame = (gameId) => {
    setPastGameId(gameId);
    openTool("pastgame");
  };

  const [poweredOff, setPoweredOff] = React.useState(false);
  // Set the instant Shut Down commits, before the 250ms end-screen
  // beat: the abandon's game_ended can round-trip inside that beat, and
  // the resulting "Timed out" notice must never flash on a machine
  // that's already turning off.
  const [poweringOff, setPoweringOff] = React.useState(false);
  // Per-icon "snap back home" functions, registered by the slots at
  // drag end — used to un-freeze the Solitaire icon if the delete
  // confirm is declined.
  const iconRevertsRef = React.useRef(new Map());
  const registerIconRevert = React.useCallback((id, fn) => {
    iconRevertsRef.current.set(id, fn);
  }, []);

  const cancelTrash = React.useCallback(() => {
    closeDialog("dlg:trash");
    const revert = iconRevertsRef.current.get("solitaire");
    if (revert) revert();
  }, [closeDialog]);

  // Kiosk mode (mouse, no keyboard — see kiosk.js): the end screen is a
  // dead end nobody at the install can leave, so there it heals itself —
  // any click, or a short wait, restarts the machine. The guard keeps
  // the tail of the OK double-click from rebooting instantly.
  const kiosk = useKiosk();
  const poweredOffAtRef = React.useRef(0);
  React.useEffect(() => {
    if (!poweredOff) return undefined;
    poweredOffAtRef.current = performance.now();
    if (!kiosk) return undefined;
    const t = setTimeout(restartMachine, KIOSK_REBOOT_MS);
    return () => clearTimeout(t);
  }, [poweredOff, kiosk]);
  const onEndScreenDown = () => {
    if (!kiosk) return;
    if (performance.now() - poweredOffAtRef.current < KIOSK_REBOOT_GUARD_MS)
      return;
    restartMachine();
  };

  // The shared power-off exit (Shut Down "Shut down", binning the
  // Solitaire icon): the game is given up immediately — an off computer
  // can't be resumed. The end screen must come up no matter what
  // window.close() does: iOS Safari can throw on tabs a script didn't
  // open; desktop browsers just ignore it.
  const powerOff = React.useCallback(() => {
    // End screen first and unconditionally — every step after is
    // best-effort, since window.close() and audio teardown can throw
    // on iOS Safari and an exception here would leave the game abandoned
    // but the end screen never shown.
    setPoweringOff(true); // suppress notices now, not after the beat
    setTimeout(() => setPoweredOff(true), 250);
    try {
      if (abandonGame) abandonGame();
    } catch {}
    try {
      powerOffAudio();
    } catch {}
    try {
      window.close();
    } catch {}
  }, [abandonGame]);

  const trashSelf = React.useCallback(() => {
    closeDialog("dlg:trash");
    // Deleting the game deletes the visit.
    powerOff();
  }, [closeDialog, powerOff]);

  // stats.txt → bin = "start my note over": confirm, zero the localStorage
  // ledger, snap the icon home — the file survives (a fresh one), so
  // nothing lives in the bin afterward.
  const cancelStatsTrash = React.useCallback(() => {
    closeDialog("dlg:trash-stats");
    const revert = iconRevertsRef.current.get("stats");
    if (revert) revert();
  }, [closeDialog]);

  const trashStats = React.useCallback(() => {
    closeDialog("dlg:trash-stats");
    clearStats();
    // An open Notepad showing the old numbers would give the delete
    // the lie — close it; reopening reads the fresh zeros.
    wmDispatch({ type: "close", id: "stats" });
    const revert = iconRevertsRef.current.get("stats");
    if (revert) revert();
  }, [closeDialog]);

  // Shut Down… is the piece's exit gesture, shaped like the real Win95
  // dialog — one modal, three radio choices. Shut down = the black end
  // screen; restart = replay the boot; stand by = the screensaver, now.
  const shutdownChoiceRef = React.useRef("shutdown");
  const cancelShutDown = React.useCallback(
    () => closeDialog("dlg:shutdown"),
    [closeDialog],
  );
  const confirmShutDown = React.useCallback(() => {
    const choice = shutdownChoiceRef.current;
    closeDialog("dlg:shutdown");
    if (choice === "shutdown") {
      // Same exit as binning the Solitaire icon.
      powerOff();
    } else if (choice === "restart") {
      restartMachine();
    } else {
      window.dispatchEvent(new Event("saat:force-idle"));
    }
  }, [closeDialog, powerOff]);

  const openShutDown = () => {
    shutdownChoiceRef.current = "shutdown";
    openDialog("dlg:shutdown", PRIORITY.CONFIRM, {
      // Radio copy lives in ShutDownChoices.
      title: "Shut Down Solitaire Alone Together",
      icon: "shutdown",
      message: <ShutDownChoices choiceRef={shutdownChoiceRef} />,
      // OK/Cancel, not Yes/No — the Win98 lead-in isn't a yes/no question.
      buttons: [
        { label: "OK", onClick: confirmShutDown },
        { label: "Cancel", onClick: cancelShutDown },
      ],
      onCancel: cancelShutDown,
    });
  };

  const iconDropped = React.useCallback(
    (id, x, y) => {
      if (id === "bin") return false;
      const bin = document.querySelector('[data-icon-slot="bin"]');
      if (!bin) return false;
      const r = bin.getBoundingClientRect();
      if (x < r.left || x > r.right || y < r.top || y > r.bottom) return false;
      if (id === "solitaire") {
        // Freeze the icon where it was dropped while the confirm is up;
        // "No" snaps it home via the registered revert.
        openDialog("dlg:trash", PRIORITY.CONFIRM, {
          title: "Confirm Delete",
          icon: "question",
          message:
            "Are you sure you want to send 'Solitaire Alone Together' to the Recycle Bin?",
          buttons: [
            { label: "Yes", onClick: trashSelf },
            { label: "No", onClick: cancelTrash },
          ],
          onCancel: cancelTrash,
        });
        return "hold";
      }
      if (id === "viewer") {
        setBinnedViewer(true);
        return "revert"; // home position again when restored from the bin
      }
      if (id === "stats") {
        openDialog("dlg:trash-stats", PRIORITY.CONFIRM, {
          title: "Confirm Delete",
          icon: "question",
          message: "Are you sure you want to clear your stats?",
          buttons: [
            { label: "Yes", onClick: trashStats },
            { label: "No", onClick: cancelStatsTrash },
          ],
          onCancel: cancelStatsTrash,
        });
        return "hold";
      }
      return false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trashSelf, cancelTrash, trashStats, cancelStatsTrash],
  );

  // Escape backs out of any transient surface, oldest desktop reflex
  // there is. (The Start menu and card selection handle their own;
  // dialogs handle Escape themselves and gate everyone else out via
  // modalGate.)
  useKey("Escape", () => {
    closePopup();
    if (chaosOpen) closeChaos();
  });

  const openChaos = () => {
    if (chaosOpen) {
      raise("chaos");
      return;
    }
    if (openGameBrowser) openGameBrowser();
    wmDispatch({ type: "open", id: "chaos", kind: KIND.TOOL });
  };
  const closeChaos = React.useCallback(() => {
    wmDispatch({ type: "close", id: "chaos" });
    if (closeGameBrowser) closeGameBrowser();
  }, [closeGameBrowser]);
  // Mobile's containing browser is the default room view. Open it once
  // when the compact desktop arrives; an explicit later close stays closed.
  const compactBrowserOpenedRef = React.useRef(false);
  React.useEffect(() => {
    if (phase !== "play" || !compact || compactBrowserOpenedRef.current) {
      return;
    }
    compactBrowserOpenedRef.current = true;
    if (openGameBrowser) openGameBrowser();
    wmDispatch({ type: "open", id: "chaos", kind: KIND.TOOL });
  }, [phase, compact, openGameBrowser]);
  // Watch is the only per-game action: put that game into the visible
  // desktop set, then close the containing browser. The server handles
  // watch before browse_close on the socket's FIFO stream.
  const watchFromChaos = (key) => {
    const id = String(key);
    const alreadyOnDesktop =
      isOpen(wmRef.current, id) &&
      visibleNeighbors.some((slot) => String(slot.key) === id);
    // Ended windows depart and reseat on their own, so a visible desktop
    // window for this id is always the live game: foreground it. Compact
    // leaves All Active Games exactly where it is — the watched window
    // rides on top of it, your own game never moves, and nothing
    // minimizes out from under your thumb.
    if (alreadyOnDesktop) {
      if (!compact) closeChaos();
      raise(id);
      setAttentionFlash({ id, toggles: 4 });
      return;
    }
    if (compact) {
      // Only compact opens a window before the server confirms — only
      // compact needs the give-up timer.
      const oldTimer = pendingWatchTimersRef.current.get(id);
      if (oldTimer != null) clearTimeout(oldTimer);
      pendingWatchTimersRef.current.set(
        id,
        setTimeout(() => {
          pendingWatchTimersRef.current.delete(id);
          wmDispatch({ type: "close", id });
        }, 5000),
      );
    }
    if (watchGame) watchGame(Number(id));
    if (compact) {
      // Open on top immediately; the board appears as soon as the watch
      // snapshot arrives. All Active Games stays open underneath — the
      // watched window is an overlay on top of it.
      wmDispatch({ type: "open", id, kind: KIND.GAME });
    } else {
      closeChaos();
    }
  };
  // Staggered neighbor reveal after the app "boots".
  const [revealedNeighbors, setRevealedNeighbors] = React.useState(0);

  // Stable scatter: slot positions fixed for the session; game ids keep
  // their slot for as long as they're present. A hand-recorded layout
  // (the Layout Studio, ?layout → layouts.js) wins over the generated
  // scatter: the nearest recorded room is stretched/clamped to this
  // viewport, and its length sets how many neighbors this room seats.
  const recordedLayout = roomLayout;
  const slotByGameRef = React.useRef(new Map());
  const slotsRef = React.useRef(null);
  const slotLayoutRef = React.useRef(null);
  if (
    slotsRef.current == null ||
    (recordedLayout && slotLayoutRef.current !== recordedLayout)
  ) {
    slotsRef.current = recordedLayout
      ? recordedLayout.neighbors.map((p) => ({ ...p }))
      : generateScatterSlots();
    slotLayoutRef.current = recordedLayout;
    slotByGameRef.current = new Map();
  }
  // Desktop right-click -> Refresh re-deals the neighbor scatter, like
  // refreshing icon layout on a real desktop. The nonce forces a render.
  // (A recorded layout is deliberate — refresh keeps it, no jitter.)
  const [, setScatterNonce] = React.useState(0);
  const refreshScatter = () => {
    if (!recordedLayout) {
      slotsRef.current = generateScatterSlots();
      slotByGameRef.current = new Map();
    }
    setScatterNonce((n) => n + 1);
  };

  // Win95 minimize/restore: a wireframe rectangle zooms between the
  // window and its taskbar button (the window itself just blinks out —
  // that's genuinely what Win95 drew). Rect of a minimized window is
  // remembered so restore can aim back at it.
  const [zoomFx, setZoomFx] = React.useState(null);
  const finishZoomFx = React.useCallback(() => setZoomFx(null), []);
  const lastWinRectRef = React.useRef(new Map());
  const wmRef = React.useRef(wm);
  wmRef.current = wm;
  const rectOf = (sel) => {
    const el = document.querySelector(sel);
    return el ? el.getBoundingClientRect() : null;
  };
  const playZoomFx = (from, to) => {
    if (!from || !to) return;
    setZoomFx({ from, to, nonce: performance.now() });
  };

  // raise/minimize are the reducer's focus/minimize plus the MinAnimate
  // wireframe side effect — the DOM rect capture stays here at the
  // edge; the pure machine never touches the DOM.
  const raise = React.useCallback((id) => {
    // Raising restores: you can't focus a window that isn't on screen.
    if (isMinimized(wmRef.current, id)) {
      playZoomFx(
        rectOf(`[data-taskbar-button="${id}"]`),
        lastWinRectRef.current.get(id),
      );
    }
    wmDispatch({ type: "focus", id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const zIndexOf = (id) => wmZIndexOf(wm, id);
  const focusedId = focused;

  const minimize = React.useCallback((id) => {
    const win = rectOf(`[data-window-box="${id}"]`);
    if (win) {
      lastWinRectRef.current.set(id, win);
      playZoomFx(win, rectOf(`[data-taskbar-button="${id}"]`));
    }
    wmDispatch({ type: "minimize", id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A game window arriving on the desktop (your own at boot, each
  // staggered neighbor, a witnessed reseat, a Watch addition) uses the
  // same Win95 gesture as a restore: a wireframe zooms from its
  // taskbar button — which appears in the same commit — to its frame,
  // and only then does the window itself exist visually. This is the
  // one arrival animation; a window always either appears instantly or
  // wireframes in. Silent.
  const [arrivingIds, setArrivingIds] = React.useState(EMPTY_ID_SET);
  const arrivalTimersRef = React.useRef(new Map());
  const prevGameWindowIdsRef = React.useRef(new Set());
  React.useEffect(
    () => () => {
      for (const t of arrivalTimersRef.current.values()) clearTimeout(t);
    },
    [],
  );
  // Layout effect: the hide must land before the browser paints the
  // freshly-opened window, or it flashes for a frame ahead of its own
  // wireframe.
  React.useLayoutEffect(() => {
    const current = new Set(
      wm.stack.filter((w) => w.kind === KIND.GAME).map((w) => w.id),
    );
    const prev = prevGameWindowIdsRef.current;
    prevGameWindowIdsRef.current = current;
    if (compact || phase !== "play") return;
    for (const id of current) {
      if (prev.has(id)) continue;
      if (quietArrivalIds.has(Number(id))) continue;
      const win = rectOf(`[data-window-box="${id}"]`);
      if (!win) continue; // not rendered this commit — appear plainly
      setArrivingIds((s) => new Set(s).add(id));
      playZoomFx(rectOf(`[data-taskbar-button="${id}"]`), win);
      const old = arrivalTimersRef.current.get(id);
      if (old != null) clearTimeout(old);
      arrivalTimersRef.current.set(
        id,
        setTimeout(() => {
          arrivalTimersRef.current.delete(id);
          setArrivingIds((s) => {
            const next = new Set(s);
            next.delete(id);
            return next;
          });
        }, ARRIVAL_HIDE_MS),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wm, compact, phase, quietArrivalIds]);

  // unwatchGame removes the id from the hook's published watched set
  // optimistically, so the slot disappears this same render — no
  // parallel suppression set needed here.
  const closeNeighbor = React.useCallback((id) => {
    if (unwatchGame) unwatchGame(Number(id));
    wmDispatch({ type: "close", id });
  }, [unwatchGame]);

  // Title-bar button clicks for game windows, wired in via cloneElement.
  const onGameControlClick = (id, control) => {
    if (control === "minimize") minimize(id);
    else if (control === "close") {
      if (id === SELF_ID) {
        if (requestDeal) requestDeal();
      } else closeNeighbor(id);
    }
  };

  // --- phase transitions ---

  // Every path out of the scene (finished, skipped, or never run)
  // converges here; the About auto-open effect below watches the same
  // states, so About appears exactly once on a first visit no matter
  // which door you left through.
  const enterPlay = React.useCallback(() => {
    try {
      // Timestamp, not a flag: firstVisit compares against it to
      // re-show the opening after a week away.
      localStorage.setItem(BOOT_SEEN_KEY, String(Date.now()));
    } catch {
      // storage denied — they'll just see the scene again next visit
    }
    setStartOpen(false);
    setSceneBeat(0);
    setSceneAct2(false);
    setPhase("play");
  }, []);

  // Rotating/resizing into compact mid-scene used to strand
  // phase="scene" forever (compact renders no scene): every system
  // notice stayed suppressed for the session and BOOT_SEEN was never
  // stamped. Snap to the desk instead.
  React.useEffect(() => {
    if (compact && (phase === "scene" || sceneAct2)) enterPlay();
  }, [compact, phase, sceneAct2, enterPlay]);

  const onLogin = () => {
    unlockAudio();
    clickSound();
    startHum(); // the monitor was always on
    // A pending system notice (the game timed out while they were away)
    // means we're resuming a dead session — playing the opening
    // ceremony and stacking About under a "Timed out" dialog would read
    // as nonsense. Reality first, no scene.
    if (firstVisit && !compact && !systemNotice) setPhase("scene");
    else enterPlay();
  };

  // Your own window exists exactly while the game does. Dispatches are
  // idempotent (the reducer answers redundant ops with the same state),
  // so this declarative sync is loop-safe.
  React.useEffect(() => {
    if (phase !== "play") return;
    if (playerGameOver) wmDispatch({ type: "close", id: SELF_ID });
    else wmDispatch({ type: "open", id: SELF_ID, kind: KIND.GAME, pin: true });
  }, [phase, playerGameOver]);

  // About auto-opens on top of the fresh game on the first visit —
  // except while the scene's act 2 is running (the ghost cursor opens
  // it through the Help menu; when act 2 ends this re-runs as a no-op
  // if the ghost succeeded, a safety net if it didn't), and except under
  // a pending system notice (About must never stack beneath a dialog).
  React.useEffect(() => {
    if (phase !== "play" || sceneAct2) return;
    if (firstVisit && !systemNotice) {
      wmDispatch({ type: "open", id: "about", kind: KIND.TOOL });
    }
    // Deliberately NOT reacting to systemNotice: once suppressed,
    // About stays away for the session instead of popping up later
    // when the notice resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, sceneAct2]);

  // Neighbors deal in one at a time once we're playing — your window
  // first, then the room assembles behind you. On a first visit the
  // trickle paces the About-reading beat (1-2s apart, still arriving
  // while the ghost hand does act 2); a plain login gets a tighter
  // stagger, the whole room seated in a few seconds.
  React.useEffect(() => {
    if (phase !== "play") return undefined;
    if (revealedNeighbors >= slotsRef.current.length) return undefined;
    const delay =
      revealedNeighbors === 0
        ? firstVisit
          ? 900
          : 500
        : firstVisit
          ? 1000 + Math.random() * 800
          : 650 + Math.random() * 350;
    const t = setTimeout(() => setRevealedNeighbors((n) => n + 1), delay);
    return () => clearTimeout(t);
  }, [phase, revealedNeighbors, firstVisit]);

  // --- scripted scene ---

  // Where the player's real mouse is, tracked through the scene so the
  // ghost can end its performance there — without this, the hidden real
  // cursor would reappear wherever it physically rested, a visible jump
  // at the handoff.
  const realMouseRef = React.useRef(null);
  React.useEffect(() => {
    const onMove = (e) => {
      realMouseRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const sceneSteps = React.useMemo(
    () => [
      { target: "start", click: true, travelMs: 1300, pauseMs: 500 },
      { target: "menu-programs", travelMs: 700, pauseMs: 550 },
      { target: "menu-solitaire", click: true, travelMs: 800, pauseMs: 700 },
      // Act 2 — the game window has booted; the same hand opens About
      // through the window's real Help menu (targets live on your
      // window only, tagged in PlayableSolitaire).
      { target: "menu-help", click: true, travelMs: 1000, pauseMs: 450 },
      { target: "menu-help-about", click: true, travelMs: 550, pauseMs: 650 },
      // Curtain call: glide to (nearly) where the real mouse is, so
      // the swap from ghost to real cursor is invisible. If the mouse
      // never moved this visit there's no position to betray — the
      // function target returns null and the beat is skipped.
      {
        target: () => {
          const m = realMouseRef.current;
          if (!m) return null;
          const zoom = readUiZoom();
          return { x: m.x / zoom - 1, y: m.y / zoom - 1 };
        },
        travelMs: 600,
        pauseMs: 150,
      },
    ],
    [],
  );
  // Act-2 beats drive the REAL MenuBar (its own component state) by
  // clicking the actual buttons — no parallel force-open plumbing.
  const sceneClick = (target) =>
    document.querySelector(`[data-scene-target="${target}"]`)?.click();
  const onSceneStep = (i) => {
    if (i === 0) setStartOpen(true);
    if (i === 1) setSceneBeat(2); // forces the Programs submenu open
    if (i === 2) {
      // The launch click: menu closes, the desk comes up, and the ghost
      // keeps the stage for act 2. If a system notice slipped in during
      // the scene (game timed out), bail to the desk instead — enterPlay
      // flips sceneAct2 off, which unmounts the ghost before act 2.
      if (systemNotice) enterPlay();
      else {
        setStartOpen(false);
        setSceneBeat(0);
        setSceneAct2(true);
        setPhase("play");
      }
    }
    if (i === 3) sceneClick("menu-help");
    if (i === 4) sceneClick("menu-help-about");
  };

  // --- slot bookkeeping ---

  const liveNeighborIdsKey = neighborSlots
    .map((slot) => String(slot.key))
    .join(",");
  const liveNeighborIds = new Set(
    neighborSlots.map((slot) => String(slot.key)),
  );
  React.useEffect(() => {
    for (const id of Array.from(pendingWatchTimersRef.current.keys())) {
      if (!liveNeighborIds.has(id)) continue;
      clearTimeout(pendingWatchTimersRef.current.get(id));
      pendingWatchTimersRef.current.delete(id);
    }
    // liveNeighborIdsKey is the stable identity; the Set is rebuilt for
    // the render-time filters below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveNeighborIdsKey]);
  const visibleNeighbors = neighborSlots.slice(
    0,
    compact ? 8 : slotsRef.current.length,
  );
  {
    // Assign/free slots. Runs during render on refs — cheap and keeps
    // assignment perfectly in sync with what we're about to draw.
    const present = new Set(visibleNeighbors.map((s) => String(s.key)));
    const map = slotByGameRef.current;
    for (const id of Array.from(map.keys())) {
      if (!present.has(id)) map.delete(id);
    }
    const used = new Set(map.values());
    for (const slot of visibleNeighbors) {
      const id = String(slot.key);
      if (map.has(id)) continue;
      for (let i = 0; i < slotsRef.current.length; i++) {
        if (!used.has(i)) {
          map.set(id, i);
          used.add(i);
          break;
        }
      }
    }
  }

  // Neighbor windows exist exactly while their game is in the visible
  // set — opened behind everything (a dealt-in stranger must never
  // cover the window you're using), closed the moment they leave.
  // Registry opens are gated on the reveal counter, so a neighbor's
  // taskbar button appears in the same commit as its window and its
  // arrival wireframe — never a bank of buttons for windows that
  // haven't landed yet.
  const visibleIdsKey = visibleNeighbors.map((s) => String(s.key)).join(",");
  React.useEffect(() => {
    if (phase !== "play") return;
    const present = new Set(visibleIdsKey ? visibleIdsKey.split(",") : []);
    const revealOrder = Array.from(slotByGameRef.current.keys());
    for (const id of present) {
      const order = revealOrder.indexOf(id);
      if (order >= 0 && order >= revealedNeighbors) continue;
      if (!isOpen(wmRef.current, id)) {
        wmDispatch({ type: "open", id, kind: KIND.GAME, behind: true });
      }
    }
    for (const w of wmRef.current.stack) {
      if (w.kind === KIND.GAME && w.id !== SELF_ID && !present.has(w.id)) {
        wmDispatch({ type: "close", id: w.id });
      }
    }
  }, [phase, visibleIdsKey, revealedNeighbors]);

  // Sync App's notice into the modal slot. A notice that loses to an
  // open confirm (lower priority) is refused now and retried when the
  // slot frees up — the wm dep re-runs this effect on every registry
  // change, and the reducer's idempotence makes the retries free.
  const noticeId = systemNotice ? `dlg:notice:${systemNotice.key}` : null;
  React.useEffect(() => {
    // Wait out the scene's act 2: a dialog popping mid-choreography
    // would stack over the ghost's Help-menu act. It appears the
    // moment the ghost yields the stage. A powered-off machine shows
    // nothing at all — the Shut Down abandon ends our own game, and the
    // resulting "Timed out" notice must not pop under the end screen.
    if (phase !== "play" || sceneAct2 || poweringOff) {
      if (poweringOff) {
        for (const w of wm.stack) {
          if (w.id.startsWith("dlg:notice:")) {
            wmDispatch({ type: "close", id: w.id });
          }
        }
      }
      return;
    }
    for (const w of wm.stack) {
      if (w.id.startsWith("dlg:notice:") && w.id !== noticeId) {
        wmDispatch({ type: "close", id: w.id });
      }
    }
    if (systemNotice) {
      wmDispatch({
        type: "open",
        id: noticeId,
        kind: KIND.DIALOG,
        modal: true,
        priority: systemNotice.priority,
        payload: systemNotice,
      });
    }
  }, [phase, sceneAct2, systemNotice, noticeId, wm, poweringOff]);

  const isActive = (id) => focusedId === id;
  // Every taskbar button carries its window's 16px icon, like the real
  // thing did. Labels/icons are presentation; existence and order come
  // from the registry (open order, like real Win95).
  const tbIcon = (name) => <PixelIcon name={name} size={16} />;
  const tbMeta = (id) => {
    if (id === SELF_ID) {
      return {
        label:
          playerGameId != null
            ? `(You) Game #${playerGameId}`
            : "Solitaire Alone Together",
        icon: tbIcon("solitaire"),
      };
    }
    if (id === "pastgame") {
      return {
        label: `(Past Game) Game #${pastGameId ?? ""}`,
        icon: tbIcon("solitaire"),
      };
    }
    const named = {
      history: { label: "Past Games", icon: tbIcon("folder") },
      about: { label: "About", icon: tbIcon("information") },
      bin: {
        label: "Recycle Bin",
        icon: tbIcon(binnedViewer ? "recycle-bin-full" : "recycle-bin-empty"),
      },
      settings: { label: "Settings", icon: tbIcon("settings") },
      stats: { label: "stats.txt", icon: tbIcon("notepad") },
      chaos: { label: "All Active Games", icon: tbIcon("all-games") },
    }[id];
    return named ?? { label: `Game #${id}`, icon: tbIcon("solitaire") };
  };
  // The rest of the room: everyone live beyond your visible neighbors.
  // One taskbar identity for it: while the window is visible it's named
  // "All Active Games"; minimized or closed, the button advertises what's
  // behind it — "and N more…" — falling back to the name when there's
  // nothing to count. Mobile always says "All Active Games" (zero seeded
  // games there; "more" than what?).
  const moreCount =
    fakeChaosCount || Math.max(0, roomActive - 1 - visibleNeighbors.length);
  const moreLabel =
    compact || moreCount === 0 ? "All Active Games" : `and ${moreCount} more…`;
  // The closed launcher waits out the arrival parade: windows and their
  // taskbar buttons appear together, and the room's "and N more…" must
  // not show up before the room itself has finished sitting down.
  const paradeDone = compact || revealedNeighbors >= slotsRef.current.length;
  const taskbarWindows = [
    ...wmTaskbarWindows(wm).map((w) => ({
      id: w.id,
      ...tbMeta(w.id),
      ...(w.id === "chaos" && w.minimized ? { label: moreLabel } : {}),
      active: isActive(w.id),
    })),
    ...(!chaosOpen && moreCount > 0 && paradeDone
      ? [
          {
            id: "chaos",
            label: moreLabel,
            icon: tbIcon("all-games"),
            active: false,
          },
        ]
      : []),
  ];

  const openTool = (id) => {
    if (isOpen(wmRef.current, id)) raise(id); // raise() plays restore fx
    else {
      wmDispatch({
        type: "open",
        id,
        kind: KIND.TOOL,
        // Display Properties is a control-panel dialog: no taskbar
        // button, exactly like the real one.
        taskbar: id !== "display",
      });
    }
  };
  const closeTool = (id) => wmDispatch({ type: "close", id });

  // Real Win95 taskbar-button behavior: restore if minimized, minimize
  // if it's the active window, otherwise bring to front.
  const onTaskbarWindowClick = (id) => {
    // The closed "and N more…" launcher is not a registry window yet.
    if (id === "chaos" && !isOpen(wm, id)) return openChaos();
    const w = getWindow(wm, id);
    if (!w) return;
    if (w.minimized) raise(id);
    else if (focusedId === id) minimize(id);
    else raise(id);
  };

  const onTaskbarWindowContextMenu = (id, e) => {
    const zoom = readUiZoom();
    openPopup({ id: "tb", winId: id, x: e.clientX / zoom, y: e.clientY / zoom });
  };

  const closeAnyWindow = (id) => {
    if (id === SELF_ID) {
      if (requestDeal) requestDeal();
    } else if (id === "chaos") {
      closeChaos();
    } else if (getWindow(wm, id)?.kind === KIND.TOOL) closeTool(id);
    else closeNeighbor(id);
  };

  const startMenuItems = [
    {
      label: "Programs",
      icon: <PixelIcon name="folder" size={16} />,
      sceneTarget: "menu-programs",
      submenu: [
        {
          label: "Solitaire Alone Together",
          icon: <PixelIcon name="solitaire" size={16} />,
          sceneTarget: "menu-solitaire",
          onClick: () => raise(SELF_ID),
        },
        {
          label: "Past Games",
          icon: <PixelIcon name="folder" size={16} />,
          onClick: () => openTool("history"),
        },
        // All Active Games is always reachable. On mobile it starts open,
        // but this entry can bring it back after an explicit close.
        {
          label: "All Active Games",
          icon: <PixelIcon name="all-games" size={16} />,
          onClick: () => openChaos(),
        },
        {
          label: "stats.txt",
          icon: <PixelIcon name="text-file" size={16} />,
          onClick: () => openTool("stats"),
        },
      ],
    },
    "separator",
    {
      label: "Settings",
      icon: <PixelIcon name="settings" size={16} />,
      onClick: () => openTool("settings"),
    },
    "separator",
    {
      label: "Deal New Game",
      icon: <PixelIcon name="solitaire" size={16} />,
      onClick: () => requestDeal && requestDeal(),
      disabled: !requestDeal,
    },
    "separator",
    {
      label: "About Solitaire…",
      icon: <PixelIcon name="information" size={16} />,
      onClick: () => openTool("about"),
    },
    "separator",
    {
      label: "Shut Down…",
      icon: <PixelIcon name="shutdown" size={16} />,
      onClick: openShutDown,
    },
  ];

  // The dialog host: whatever modal the registry holds renders here,
  // through the one MessageBox. Keyed by id so a replacement (still-
  // there → timed-out) remounts and re-arms the button guard.
  const dialogHost = modalWin && modalWin.payload && (
    <DialogHost>
      <MessageBox
        key={modalWin.id}
        title={modalWin.payload.title}
        icon={modalWin.payload.icon}
        buttons={modalWin.payload.buttons}
        onCancel={modalWin.payload.onCancel}
      >
        {modalWin.payload.message}
      </MessageBox>
    </DialogHost>
  );

  // The exit: black screen, machine off. Verbatim canon copy.
  const endScreen = poweredOff && (
    <EndScreen onMouseDown={onEndScreenDown}>
      <p>It&apos;s now safe to close this tab.</p>
    </EndScreen>
  );

  const inScene = phase === "scene";
  const inPlay = phase === "play";

  // ------------------------------------------------------------------
  // Compact (phone) layout: login gate, then a compact desktop. All Active
  // Games is the room view and starts open; ordinary watched windows are
  // zero. Watch adds explicit game windows to this desktop. No scripted
  // scene on phones.
  // ------------------------------------------------------------------
  if (compact) {
    // Sideways phone: not enough height for a stacked column, so the
    // browser sits beside your game instead of above it.
    const isLandscape =
      viewportHeight <= 520 && viewportWidth > viewportHeight;
    const gameScale = isLandscape
      ? Math.min(
          1,
          (viewportHeight - TASKBAR_HEIGHT - 16) / GAME_WINDOW_HEIGHT,
          ((viewportWidth - 12) * 0.62) / GAME_WINDOW_WIDTH,
        )
      : Math.min(1, (viewportWidth - 8) / GAME_WINDOW_WIDTH);
    // 0.5 exactly: halving keeps the pixel font/sprites legible in a way
    // arbitrary fractions don't.
    const neighborScale = 0.5;
    // Compact shows ONE tool window at a time: the topmost open one in
    // the registry (raise-on-open makes taskbar clicks switch between
    // them). The old fixed priority chain meant opening the bin while
    // the viewer was up showed a taskbar button for… nothing.
    const COMPACT_TOOLS = ["history", "bin", "stats", "about", "settings"];
    const compactTool =
      [...wm.stack]
        .filter((w) => COMPACT_TOOLS.includes(w.id) && !w.minimized)
        .pop()?.id ?? null;
    const compactTaskbarWindows = wmTaskbarWindows(wm).map((w) => ({
      id: w.id,
      ...tbMeta(w.id),
      active: focusedId === w.id,
    }));
    // The full desktop's icon set, compact-sized. The Solitaire icon
    // scrolls your always-present game into view — same gesture as its
    // taskbar button.
    const scrollCompactSelf = () => {
      raise(SELF_ID);
      document
        .querySelector("[data-compact-self]")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    const openCompactChaos = () => {
      openChaos();
      requestAnimationFrame(() =>
        document
          .querySelector("[data-compact-chaos]")
          ?.scrollIntoView({ behavior: "smooth", block: "center" }),
      );
    };
    const compactIcons = (
      <CompactIconStrip>
        <DesktopIcon
          icon={<PixelIcon name="solitaire" size={32} />}
          label="Solitaire Alone Together"
          onOpen={scrollCompactSelf}
        />
        <DesktopIcon
          icon={<PixelIcon name="folder" size={32} />}
          label="Past Games"
          onOpen={() => openTool("history")}
        />
        <DesktopIcon
          icon={<PixelIcon name="all-games" size={32} />}
          label="All Active Games"
          onOpen={openCompactChaos}
        />
        <DesktopIcon
          icon={<PixelIcon name="text-file" size={32} />}
          label="stats.txt"
          onOpen={() => openTool("stats")}
        />
        <CompactBinSlot>
          <RecycleBinIcon full={binnedViewer} onOpen={() => openTool("bin")} />
        </CompactBinSlot>
      </CompactIconStrip>
    );
    return (
      <Viewport
        onMouseDown={() => setStartOpen(false)}
        // Long-press on phones spawns the browser context menu over the
        // '95 chrome; the desktop branch suppresses it, compact now too.
        onContextMenu={(e) => e.preventDefault()}
      >
        {phase === "post" ? (
          <BootScreen instant />
        ) : phase === "login" ? (
          <LoginDialog onEnter={onLogin} />
        ) : (
          <CompactColumn
            data-landscape={isLandscape ? "true" : undefined}
            style={{ "--taskbar-h": TASKBAR_HEIGHT + "px" }}
          >
            {!isLandscape && compactIcons}

            {/* Stays mounted while minimized, exactly like desktop: the
                suspended prop empties the browse_set (no board streams to
                a taskbar'd window) and display:none preserves scroll
                position and column state for an instant restore. */}
            {isOpen(wm, "chaos") && (
              <Hidden
                data-hidden={isMinimized(wm, "chaos") ? "true" : undefined}
              >
                <TouchDragBox
                  fill
                  data-compact-chaos="true"
                  onActivate={() => raise("chaos")}
                >
                  <ActiveGamesWindow
                    gameIds={activeBrowserIds}
                    endedGameIds={activeBrowserEndedIds}
                    readyGameIds={activeBrowserReadyIds}
                    finalStates={browseFinalStates}
                    gameSlots={[...browserGameSlots, ...fakeChaosSlots]}
                    watchDisabledIds={fakeChaosIdSet}
                    fill
                    inactive={focusedId !== "chaos"}
                    suspended={isMinimized(wm, "chaos")}
                    onRangeChange={updateBrowserRange}
                    onLoadFinal={loadBrowseFinal}
                    onWatch={watchFromChaos}
                    onMinimize={() => minimize("chaos")}
                    onClose={closeChaos}
                  />
                </TouchDragBox>
              </Hidden>
            )}

            {isLandscape ? (
              <LandscapeRail>
                {compactIcons}
                {!playerGameOver && (
                  <TouchDragBox
                    data-compact-self="true"
                    style={{
                      "--w": Math.ceil(GAME_WINDOW_WIDTH * gameScale) + "px",
                      "--h": Math.ceil(GAME_WINDOW_HEIGHT * gameScale) + "px",
                    }}
                  >
                    <ScaleBox style={{ "--scale": gameScale }}>
                      {React.cloneElement(playerSlot, {
                        dealNewGame: requestDeal,
                        // Close = walk away (same confirm as desktop);
                        // minimize has nowhere to go in the dock.
                        disabledControls: ["minimize", "maximize"],
                        onControlClick: (control) =>
                          control === "close" && requestDeal && requestDeal(),
                      })}
                    </ScaleBox>
                  </TouchDragBox>
                )}
              </LandscapeRail>
            ) : !playerGameOver ? (
              <TouchDragBox
                data-compact-self="true"
                style={{
                  "--w": Math.ceil(GAME_WINDOW_WIDTH * gameScale) + "px",
                  "--h": Math.ceil(GAME_WINDOW_HEIGHT * gameScale) + "px",
                }}
              >
                <ScaleBox style={{ "--scale": gameScale }}>
                  {React.cloneElement(playerSlot, {
                    dealNewGame: requestDeal,
                    disabledControls: ["minimize", "maximize"],
                    onControlClick: (control) =>
                      control === "close" && requestDeal && requestDeal(),
                  })}
                </ScaleBox>
              </TouchDragBox>
            ) : null}
          </CompactColumn>
        )}

        {phase === "play" &&
          visibleNeighbors.map((slot) => {
            const id = String(slot.key);
            if (!isOpen(wm, id) || isMinimized(wm, id)) return null;
            return (
              <CompactGameLayer key={id} style={{ "--z": 100 + zIndexOf(id) }}>
                <TouchDragBox
                  data-compact-game={id}
                  onActivate={() => raise(id)}
                  style={{
                    "--w": Math.ceil(GAME_WINDOW_WIDTH * neighborScale) + "px",
                    "--h": Math.ceil(GAME_WINDOW_HEIGHT * neighborScale) + "px",
                  }}
                >
                  <ScaleBox style={{ "--scale": neighborScale }}>
                    {React.cloneElement(slot, {
                      inactive:
                        focusedId !== id ||
                        (attentionFlash?.id === id &&
                          attentionFlash.toggles % 2 === 1),
                      disabledControls: ["maximize"],
                      onControlClick: (control) =>
                        onGameControlClick(id, control),
                      onHelpAbout: () => openTool("about"),
                      onDeparted: () => departGame?.(Number(id)),
                    })}
                  </ScaleBox>
                </TouchDragBox>
              </CompactGameLayer>
            );
          })}

        {phase === "play" &&
          pastGameId != null &&
          isOpen(wm, "pastgame") &&
          !isMinimized(wm, "pastgame") && (
            <CompactGameLayer style={{ "--z": 100 + zIndexOf("pastgame") }}>
              {/* Half-size assets, full-size chrome: the window sizes
                  itself (boardScale) — no whole-window ScaleBox here. */}
              <TouchDragBox
                data-compact-game="pastgame"
                onActivate={() => raise("pastgame")}
                style={{
                  "--w": scaledWindowSize(0.5).width + "px",
                  "--h": scaledWindowSize(0.5).height + "px",
                }}
              >
                <PastGameWindow
                  gameId={pastGameId}
                  inactive={focusedId !== "pastgame"}
                  onClose={() => closeTool("pastgame")}
                />
              </TouchDragBox>
            </CompactGameLayer>
          )}

        {compactTool != null && (
          <CompactToolLayer style={{ "--z": 100 + zIndexOf(compactTool) }}>
            {compactTool === "history" && (
              <CompactScaler
                style={{ "--scale": gameScale, "--w": "620px", "--h": "440px" }}
              >
                <ScaleBox style={{ "--scale": gameScale }}>
                  <HistoryWindow
                    onClose={() => closeTool("history")}
                    onOpenGame={openPastGame}
                  />
                </ScaleBox>
              </CompactScaler>
            )}
            {compactTool === "bin" && (
              <RecycleBinWindow onClose={() => closeTool("bin")} />
            )}
            {compactTool === "stats" && (
              <StatsWindow onClose={() => closeTool("stats")} />
            )}
            {compactTool === "about" && (
              <AboutWindow onClose={() => closeTool("about")} />
            )}
            {compactTool === "settings" && (
              <SettingsWindow onClose={() => closeTool("settings")} />
            )}
          </CompactToolLayer>
        )}

        <Taskbar
          windows={phase === "login" ? [] : compactTaskbarWindows}
          onWindowClick={(id) => {
            // Your own game remains docked in the scroll column.
            if (id === SELF_ID) {
              raise(id);
              document
                .querySelector("[data-compact-self]")
                ?.scrollIntoView({ behavior: "smooth", block: "center" });
              return;
            }
            const restoringBrowser = id === "chaos" && isMinimized(wm, "chaos");
            onTaskbarWindowClick(id);
            if (restoringBrowser) {
              requestAnimationFrame(() =>
                document
                  .querySelector("[data-compact-chaos]")
                  ?.scrollIntoView({ behavior: "smooth", block: "center" }),
              );
            }
          }}
          startOpen={startOpen}
          onStartToggle={() => setStartOpen(!startOpen)}
        />
        <StartMenu
          open={startOpen}
          onClose={() => setStartOpen(false)}
          items={startMenuItems}
          banner="Solitaire Alone Together"
        />
        {dialogHost}
        {/* Shut Down must render here too — the desktop branch below
            always renders it. */}
        {endScreen}
      </Viewport>
    );
  }

  // ------------------------------------------------------------------
  // Desktop
  // ------------------------------------------------------------------
  return (
    <Viewport
      data-bezel="true"
      data-hide-cursor={inScene || sceneAct2 ? "true" : undefined}
      // Sound rides the CAPTURE phase so a child's stopPropagation (Start
      // button, menus — they stop the bubble so closePopup can't eat the
      // popup they just opened) can never swallow the click. closePopup
      // stays on the bubble phase for exactly that reason.
      onMouseDownCapture={clickSound /* no-ops until audio is unlocked */}
      onMouseDown={closePopup}
      onContextMenu={(e) => {
        // Right-click is Windows right-click everywhere — never the
        // browser menu. The desktop surface gets its menu during play;
        // other surfaces do nothing until they earn a real menu.
        e.preventDefault();
        if (!inPlay) return;
        if (e.target.closest(".window")) return;
        const zoom = readUiZoom();
        openPopup({ id: "ctx", x: e.clientX / zoom, y: e.clientY / zoom });
      }}
    >
      {phase === "post" && <BootScreen instant />}

      {/* Desktop icons — on the wallpaper, under all windows. The
          desktop stays bare (no icons, no taskbar) until the machine
          has booted and someone logged in. Each icon is draggable
          (Win95 with auto-arrange off) and remembers where you left it. */}
      {(inScene || inPlay) && (
        <>
          <DraggableIconSlot
            id="solitaire"
            anchor={{ left: 10, top: 12 }}
            onDropped={iconDropped}
            registerRevert={registerIconRevert}
          >
            <DesktopIcon
              icon={<PixelIcon name="solitaire" size={32} />}
              label="Solitaire Alone Together"
              onOpen={() => raise(SELF_ID)}
            />
          </DraggableIconSlot>
          {!binnedViewer && (
            <DraggableIconSlot
              id="viewer"
              anchor={{ left: 10, top: 116 }}
              onDropped={iconDropped}
              registerRevert={registerIconRevert}
            >
              <DesktopIcon
                icon={<PixelIcon name="folder" size={32} />}
                label="Past Games"
                onOpen={() => openTool("history")}
              />
            </DraggableIconSlot>
          )}
          <DraggableIconSlot
            id="viewall"
            anchor={{ left: 10, top: 220 }}
            onDropped={iconDropped}
            registerRevert={registerIconRevert}
          >
            <DesktopIcon
              icon={<PixelIcon name="all-games" size={32} />}
              label="All Active Games"
              onOpen={() => openChaos()}
            />
          </DraggableIconSlot>
          <DraggableIconSlot
            id="stats"
            anchor={{ left: 10, top: 324 }}
            onDropped={iconDropped}
            registerRevert={registerIconRevert}
          >
            <DesktopIcon
              icon={<PixelIcon name="text-file" size={32} />}
              label="stats.txt"
              onOpen={() => openTool("stats")}
            />
          </DraggableIconSlot>
          <DraggableIconSlot
            id="bin"
            anchor={{ left: 10, bottom: TASKBAR_HEIGHT + 10 }}
          >
            <RecycleBinIcon
              full={binnedViewer}
              onOpen={() => openTool("bin")}
            />
          </DraggableIconSlot>
        </>
      )}

      {inPlay && (
        <World>
          {visibleNeighbors.map((slot) => {
            const id = String(slot.key);
            const slotIdx = slotByGameRef.current.get(id) ?? 0;
            const pos = slotsRef.current[slotIdx];
            const order = Array.from(slotByGameRef.current.keys()).indexOf(id);
            if (order >= revealedNeighbors) return null;
            // Render only once the registry holds the window: the frame,
            // its taskbar button, and the arrival wireframe hide all land
            // in the same commit — no one-frame flash before the outline.
            if (!isOpen(wm, id)) return null;
            return (
              <Hidden
                key={slot.key}
                data-hidden={isMinimized(wm, id) ? "true" : undefined}
                data-arriving={arrivingIds.has(id) ? "true" : undefined}
              >
                <PositionedWindow
                  position={pos}
                  draggable
                  zIndex={zIndexOf(id)}
                  boxId={id}
                  onActivate={() => raise(id)}
                >
                  {React.cloneElement(slot, {
                    inactive:
                      focusedId !== id ||
                      (attentionFlash?.id === id &&
                        attentionFlash.toggles % 2 === 1),
                    onControlClick: (control) =>
                      onGameControlClick(id, control),
                    onHelpAbout: () => openTool("about"),
                    onDeparted: () => departGame?.(Number(id)),
                  })}
                </PositionedWindow>
              </Hidden>
            );
          })}
          {!playerGameOver && isOpen(wm, SELF_ID) && (
            <Hidden
              data-hidden={isMinimized(wm, SELF_ID) ? "true" : undefined}
              data-arriving={arrivingIds.has(SELF_ID) ? "true" : undefined}
            >
              <PositionedWindow
                position={recordedLayout?.self ?? { x: 0, y: 0 }}
                draggable
                zIndex={zIndexOf(SELF_ID)}
                boxId={SELF_ID}
                onActivate={() => raise(SELF_ID)}
              >
                {React.cloneElement(playerSlot, {
                  inactive: focusedId !== SELF_ID,
                  onControlClick: (control) =>
                    onGameControlClick(SELF_ID, control),
                  onHelpAbout: () => openTool("about"),
                  dealNewGame: requestDeal,
                })}
              </PositionedWindow>
            </Hidden>
          )}
        </World>
      )}

      {inPlay && (
        <ToolLayer>
          {isOpen(wm, "history") && (
            <Hidden data-hidden={isMinimized(wm, "history") ? "true" : undefined}>
              <PositionedWindow
                position={{ x: 0, y: -40 }}
                draggable
                zIndex={zIndexOf("history")}
                boxId="history"
                onActivate={() => raise("history")}
              >
                <HistoryWindow
                  inactive={focusedId !== "history"}
                  onClose={() => closeTool("history")}
                  onHelpAbout={() => openTool("about")}
                  onOpenGame={openPastGame}
                />
              </PositionedWindow>
            </Hidden>
          )}
          {isOpen(wm, "pastgame") && pastGameId != null && (
            <Hidden
              data-hidden={isMinimized(wm, "pastgame") ? "true" : undefined}
            >
              <PositionedWindow
                position={{ x: 60, y: -10 }}
                draggable
                zIndex={zIndexOf("pastgame")}
                boxId="pastgame"
                onActivate={() => raise("pastgame")}
              >
                <PastGameWindow
                  gameId={pastGameId}
                  inactive={focusedId !== "pastgame"}
                  onClose={() => closeTool("pastgame")}
                />
              </PositionedWindow>
            </Hidden>
          )}
          {isOpen(wm, "chaos") && (
            <Hidden data-hidden={isMinimized(wm, "chaos") ? "true" : undefined}>
              <PositionedWindow
                position={{ x: 0, y: -TASKBAR_HEIGHT / 2 }}
                draggable
                zIndex={zIndexOf("chaos")}
                boxId="chaos"
                onActivate={() => raise("chaos")}
              >
                <ActiveGamesWindow
                  gameIds={activeBrowserIds}
                  endedGameIds={activeBrowserEndedIds}
                  readyGameIds={activeBrowserReadyIds}
                  finalStates={browseFinalStates}
                  gameSlots={[...browserGameSlots, ...fakeChaosSlots]}
                  watchDisabledIds={fakeChaosIdSet}
                  width={activeBrowserWidth}
                  height={activeBrowserHeight}
                  inactive={focusedId !== "chaos"}
                  suspended={isMinimized(wm, "chaos")}
                  onRangeChange={updateBrowserRange}
                  onLoadFinal={loadBrowseFinal}
                  onWatch={watchFromChaos}
                  onMinimize={() => minimize("chaos")}
                  onClose={closeChaos}
                />
              </PositionedWindow>
            </Hidden>
          )}
          {isOpen(wm, "about") && (
            <Hidden data-hidden={isMinimized(wm, "about") ? "true" : undefined}>
              <PositionedWindow
                position={{ x: 30, y: -10 }}
                draggable
                zIndex={zIndexOf("about")}
                boxId="about"
                onActivate={() => raise("about")}
              >
                <AboutWindow
                  inactive={focusedId !== "about"}
                  onClose={() => closeTool("about")}
                />
              </PositionedWindow>
            </Hidden>
          )}
          {isOpen(wm, "stats") && (
            <Hidden data-hidden={isMinimized(wm, "stats") ? "true" : undefined}>
              <PositionedWindow
                position={{ x: 60, y: 30 }}
                draggable
                zIndex={zIndexOf("stats")}
                boxId="stats"
                onActivate={() => raise("stats")}
              >
                <StatsWindow
                  inactive={focusedId !== "stats"}
                  onClose={() => closeTool("stats")}
                />
              </PositionedWindow>
            </Hidden>
          )}
          {isOpen(wm, "bin") && (
            <Hidden data-hidden={isMinimized(wm, "bin") ? "true" : undefined}>
              <PositionedWindow
                position={{ x: -180, y: 120 }}
                draggable
                zIndex={zIndexOf("bin")}
                boxId="bin"
                onActivate={() => raise("bin")}
              >
                <RecycleBinWindow
                  inactive={focusedId !== "bin"}
                  onClose={() => closeTool("bin")}
                  items={
                    binnedViewer
                      ? [
                          {
                            label: "Past Games",
                            iconName: "folder",
                            onRestore: () => setBinnedViewer(false),
                          },
                        ]
                      : []
                  }
                />
              </PositionedWindow>
            </Hidden>
          )}
          {isOpen(wm, "settings") && (
            <Hidden data-hidden={isMinimized(wm, "settings") ? "true" : undefined}>
              <PositionedWindow
                position={{ x: -140, y: -90 }}
                draggable
                zIndex={zIndexOf("settings")}
                boxId="settings"
                onActivate={() => raise("settings")}
              >
                <SettingsWindow
                  inactive={focusedId !== "settings"}
                  onClose={() => closeTool("settings")}
                />
              </PositionedWindow>
            </Hidden>
          )}
          {isOpen(wm, "display") && (
            <Hidden data-hidden={isMinimized(wm, "display") ? "true" : undefined}>
              <PositionedWindow
                position={{ x: 160, y: -120 }}
                draggable
                zIndex={zIndexOf("display")}
                boxId="display"
                onActivate={() => raise("display")}
              >
                <DisplayProperties
                  inactive={focusedId !== "display"}
                  onClose={() => closeTool("display")}
                />
              </PositionedWindow>
            </Hidden>
          )}
        </ToolLayer>
      )}

      {ctxMenu && (
        <CtxMenu
          className="window"
          style={{ "--cx": ctxMenu.x + "px", "--cy": ctxMenu.y + "px" }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <CtxItem
            type="button"
            onClick={() => {
              closePopup();
              refreshScatter();
            }}
          >
            Refresh
          </CtxItem>
          <CtxSeparator />
          <CtxItem
            type="button"
            onClick={() => {
              closePopup();
              openTool("display");
            }}
          >
            Properties
          </CtxItem>
        </CtxMenu>
      )}

      {(inScene || inPlay) && (
        <>
          <Taskbar
            windows={inPlay ? taskbarWindows : []}
            onWindowClick={onTaskbarWindowClick}
            onWindowContextMenu={onTaskbarWindowContextMenu}
            startOpen={startOpen}
            onStartToggle={() => setStartOpen(!startOpen)}
          />
          <StartMenu
            open={startOpen}
            onClose={() => setStartOpen(false)}
            items={startMenuItems}
            banner="Solitaire Alone Together"
            forceSubmenuFor={sceneBeat >= 2 ? "Programs" : undefined}
            forceHoverFor={
              sceneBeat === 2
                ? "menu-programs"
                : sceneBeat === 3
                  ? "menu-solitaire"
                  : undefined
            }
          />
        </>
      )}

      {taskbarMenu && (
        <CtxMenu
          className="window"
          data-up="true"
          style={{
            "--cx": taskbarMenu.x + "px",
            "--cy": taskbarMenu.y + "px",
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {isMinimized(wm, taskbarMenu.winId) ? (
            <CtxItem
              type="button"
              onClick={() => {
                raise(taskbarMenu.winId);
                closePopup();
              }}
            >
              Restore
            </CtxItem>
          ) : (
            <CtxItem
              type="button"
              onClick={() => {
                minimize(taskbarMenu.winId);
                closePopup();
              }}
            >
              Minimize
            </CtxItem>
          )}
          <CtxSeparator />
          <CtxItem
            type="button"
            onClick={() => {
              closeAnyWindow(taskbarMenu.winId);
              closePopup();
            }}
          >
            Close
          </CtxItem>
        </CtxMenu>
      )}

      {dialogHost}
      {endScreen}

      {zoomFx && (
        <ZoomFx
          key={zoomFx.nonce}
          fx={zoomFx}
          onDone={finishZoomFx}
        />
      )}

      {phase === "login" && <LoginDialog onEnter={onLogin} />}

      {(inScene || sceneAct2) && (
        <>
          <GhostCursor
            steps={sceneSteps}
            startAt={sceneStartPosition()}
            onStep={onSceneStep}
            onDone={enterPlay}
          />
          {/* Invisible blocker: real input is paused during the scene
              (both acts); any click skips to the desk — enterPlay's
              About effect covers whatever the ghost didn't get to. */}
          <SceneBlocker onMouseDown={enterPlay} />
        </>
      )}
    </Viewport>
  );
}

// The minimize/restore wireframe, the way Win95/98's MinAnimate actually
// drew it: a handful of discrete rectangle outlines stepping from source
// to destination — chunky frames, no easing, no tween. 5 frames at 38ms
// reads as visibly stepped. Coordinates are raw client px (zoom is 1).
const ZOOM_FX_FRAMES = 5;
const ZOOM_FX_FRAME_MS = 38;
// An arriving window stays invisible until its wireframe has landed
// (plus a beat so the last outline frame isn't cut off mid-hold).
const ARRIVAL_HIDE_MS = ZOOM_FX_FRAMES * ZOOM_FX_FRAME_MS + 30;

function ZoomFx({ fx, onDone }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const lerp = (a, b, t) => Math.round(a + (b - a) * t);
    const drawFrame = (i) => {
      const t = i / (ZOOM_FX_FRAMES - 1);
      el.style.left = lerp(fx.from.left, fx.to.left, t) + "px";
      el.style.top = lerp(fx.from.top, fx.to.top, t) + "px";
      el.style.width = lerp(fx.from.width, fx.to.width, t) + "px";
      el.style.height = lerp(fx.from.height, fx.to.height, t) + "px";
    };
    let frame = 0;
    drawFrame(0);
    const interval = setInterval(() => {
      frame += 1;
      if (frame >= ZOOM_FX_FRAMES) {
        clearInterval(interval);
        onDone();
        return;
      }
      drawFrame(frame);
    }, ZOOM_FX_FRAME_MS);
    return () => clearInterval(interval);
  }, [fx, onDone]);
  return <FxRect ref={ref} aria-hidden="true" />;
}

const FxRect = styled.div`
  position: fixed;
  z-index: 2600;
  pointer-events: none;
  border: 2px solid #dfdfdf;
  box-shadow:
    0 0 0 1px #404040,
    inset 0 0 0 1px #404040;
`;

// Shut Down → Restart: reload into the boot sequence.
function restartMachine() {
  const url = new URL(window.location.href);
  url.search = "?boot";
  window.location.href = url.toString();
}

const KIOSK_REBOOT_MS = 20000;
const KIOSK_REBOOT_GUARD_MS = 1000;

function sceneStartPosition() {
  const zoom = readUiZoom();
  return {
    x: window.innerWidth / zoom / 2 + 120,
    y: window.innerHeight / zoom / 2 - 60,
  };
}

const Viewport = styled.div`
  position: fixed;
  inset: 0;
  /* iOS Safari: the layout viewport extends under the collapsing URL
     bar, so bottom: 0 would bury the taskbar. dvh tracks what's truly
     visible; vh line is the fallback for older browsers. */
  bottom: auto;
  height: 100vh;
  height: 100dvh;
  /* clip, not hidden: hidden boxes can still be scrolled
     programmatically (scrollIntoView from keyboard focus, find-in-
     page), which would drag the whole desktop — taskbar included —
     off its anchors. clip forbids scrolling outright. */
  overflow: hidden;
  overflow: clip;
  /* The desktop color lives here (body behind it is black bezel). */
  background: var(--desktop-bg);

  /* Desktop only: inset the whole screen a hair inside a black bezel
     with softly rounded corners, so rounded physical displays never
     clip the taskbar corners — and the screen reads a little more like
     glass in a monitor. */
  &[data-bezel="true"] {
    top: 5px;
    left: 5px;
    right: 5px;
    height: calc(100vh - 10px);
    height: calc(100dvh - 10px);
    border-radius: 8px;
  }

  /* One machine, one cursor: the whole desktop (and everything on it)
     uses the period arrow — the same drawing the ghost cursor and the
     neighbors' remote cursors use. !important flattens the browser's
     per-widget cursors (pointer over buttons, I-beam, grab on title
     bars); Win95 showed the arrow for all of those. */
  &,
  & * {
    cursor: url(${cursorArrowUrl}) 1 1, default !important;
  }

  &[data-hide-cursor="true"],
  &[data-hide-cursor="true"] * {
    cursor: none !important;
  }
`;

// Centered, unscaled coordinate space for the game windows. No z-index
// of its own — every window (games AND tools) carries its zOrder value
// and they all stack in one shared space, so anything can be raised
// above anything. Desktop icons sit at 1, taskbar at 1000.
const World = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
`;

// display: contents so it never disturbs positioning; display: none when
// its window is minimized (kept mounted — spectator subscriptions and
// the player's own game state survive the round trip to the taskbar).
const Hidden = styled.div`
  display: contents;

  &[data-hidden="true"] {
    display: none;
  }

  /* Mid-arrival: the window occupies its spot (the wireframe needs a
     real rect to zoom to) but stays invisible until the outline lands.
     visibility inherits straight through display: contents. */
  &[data-arriving="true"] {
    visibility: hidden;
  }
`;

// ---- draggable desktop icons ----

// Each icon has a home slot (its CSS anchor) plus a persisted drag
// offset, so a rearranged desktop survives reload — your machine, your
// mess. Offsets, not absolute positions: an undragged Recycle Bin stays
// glued to the bottom corner across window resizes.
const ICON_POS_KEY = "saat.iconPos.v1";

function readIconOffsets() {
  try {
    return JSON.parse(localStorage.getItem(ICON_POS_KEY)) || {};
  } catch {
    return {};
  }
}

function writeIconOffset(id, offset) {
  try {
    const all = readIconOffsets();
    all[id] = offset;
    localStorage.setItem(ICON_POS_KEY, JSON.stringify(all));
  } catch {
    // storage unavailable — session-only positions are fine
  }
}

// `onDropped(id, clientX, clientY)` fires after a real drag ends and
// says what happens to the icon:
//   false     — normal drop: stays put, position persists
//   "revert"  — snaps home immediately (drop consumed the icon)
//   "hold"    — FREEZES where dropped, nothing persisted; the owner
//               later calls the function passed to `registerRevert` to
//               snap it back (confirm-dialog flows).
// `registerRevert(id, fn)` hands the owner a revert function for this
// slot. While dragging, the slot marks the bin slot with
// data-drop-target when hovering it, like a real drop target.
function DraggableIconSlot({ id, anchor, onDropped, registerRevert, children }) {
  const [offset, setOffset] = React.useState(() => {
    const o = readIconOffsets()[id];
    return o && Number.isFinite(o.x) && Number.isFinite(o.y)
      ? o
      : { x: 0, y: 0 };
  });
  // While in-flight the icon rides above the windows (you're holding
  // it); at rest it lives on the wallpaper beneath them.
  const [dragging, setDragging] = React.useState(false);
  const rootRef = React.useRef(null);

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    const el = rootRef.current;
    if (!el) return;
    const zoom = readUiZoom();
    const rect = el.getBoundingClientRect();
    // Movement bounds: the icon stays fully on the desktop (above the
    // taskbar) — Windows never let you strand an icon off-screen.
    const minDx = -rect.left / zoom;
    const maxDx = (window.innerWidth - rect.right) / zoom;
    const minDy = -rect.top / zoom;
    const maxDy =
      (window.innerHeight - rect.bottom) / zoom - TASKBAR_HEIGHT;
    const startX = e.clientX;
    const startY = e.clientY;
    const base = offset;
    let latest = base;
    let moved = false;
    const binEl =
      id === "bin" ? null : document.querySelector('[data-icon-slot="bin"]');
    const overBin = (ev) => {
      if (!binEl) return false;
      const r = binEl.getBoundingClientRect();
      return (
        ev.clientX >= r.left &&
        ev.clientX <= r.right &&
        ev.clientY >= r.top &&
        ev.clientY <= r.bottom
      );
    };
    const markBin = (hot) => {
      if (!binEl) return;
      if (hot) binEl.setAttribute("data-drop-target", "true");
      else binEl.removeAttribute("data-drop-target");
    };
    const onMove = (ev) => {
      const dx = (ev.clientX - startX) / zoom;
      const dy = (ev.clientY - startY) / zoom;
      // Small threshold so a plain click (select / double-click open)
      // never registers as a drag.
      if (!moved && Math.hypot(dx, dy) < 4) return;
      if (!moved) setDragging(true);
      moved = true;
      latest = {
        x: base.x + Math.max(minDx, Math.min(maxDx, dx)),
        y: base.y + Math.max(minDy, Math.min(maxDy, dy)),
      };
      setOffset(latest);
      markBin(overBin(ev));
    };
    const onUp = (ev) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      markBin(false);
      if (!moved) {
        setDragging(false);
        return;
      }
      if (registerRevert) {
        registerRevert(id, () => {
          setOffset(base);
          setDragging(false);
        });
      }
      const verdict =
        onDropped && onDropped(id, ev.clientX ?? 0, ev.clientY ?? 0);
      if (verdict === "revert") {
        setOffset(base);
        setDragging(false);
      } else if (verdict === "hold") {
        // Frozen over the bin under the Confirm Delete box: stay ON TOP
        // (you're mid-gesture until you answer); the registered revert
        // drops the icon back under the windows on "No".
      } else {
        setDragging(false);
        writeIconOffset(id, latest);
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  return (
    <IconSlot
      ref={rootRef}
      data-icon-slot={id}
      data-dragging={dragging ? "true" : undefined}
      onPointerDown={onPointerDown}
      style={{
        ...anchor,
        "--ix": offset.x + "px",
        "--iy": offset.y + "px",
      }}
    >
      {children}
    </IconSlot>
  );
}

const IconSlot = styled.div`
  position: absolute;
  z-index: 1;
  transform: translate(var(--ix, 0px), var(--iy, 0px));
  touch-action: none;

  /* An icon you're holding rides above everything short of the taskbar
     and dialogs — same as dragging a desktop icon over windows in real
     Win95. At rest it drops back under the windows. */
  &[data-dragging="true"] {
    z-index: 950;
  }

  /* Drop-target feedback while another icon is dragged over this one
     (only ever set on the bin): label lights up like a selection. */
  &[data-drop-target="true"] button span:last-child {
    background: #00007b;
    color: #fff;
    text-shadow: none;
    outline: 1px dotted #ffff7b;
  }
`;

// Same coordinate space as World; z comes from each window's zOrder.
const ToolLayer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
`;

const CtxMenu = styled.div`
  position: absolute;
  left: var(--cx);
  top: var(--cy);
  z-index: 1200;
  display: flex;
  flex-direction: column;
  min-width: 150px;
  padding: 2px;

  /* Taskbar-button menus open upward, like the real thing. */
  &[data-up="true"] {
    transform: translateY(-100%);
  }
`;

// The modal layer: above every window, the taskbar, and its menus —
// MessageBox's own scrim does the click-swallowing (and answers with
// the ding + title-bar flash).
const DialogHost = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3000;
`;

// The Shut Down dialog's body uses Win98's phrasing (Stand by was a
// Win98 addition; Win95's "Are you sure you want to:" reads oddly next
// to it). Selection state lives here (the modal registry payload is
// inert), mirrored into choiceRef so the OK button can read the answer.
function ShutDownChoices({ choiceRef }) {
  const [choice, setChoice] = React.useState(choiceRef.current);
  const pick = (v) => {
    choiceRef.current = v;
    setChoice(v);
  };
  const options = [
    ["standby", "Stand by"],
    ["shutdown", "Shut down"],
    ["restart", "Restart"],
  ];
  return (
    <ShutDownBody>
      <p>What do you want the computer to do?</p>
      {options.map(([value, label]) => (
        <div className="field-row" key={value}>
          <input
            type="radio"
            id={`shutdown-${value}`}
            name="shutdown-choice"
            checked={choice === value}
            onChange={() => pick(value)}
          />
          <label htmlFor={`shutdown-${value}`}>{label}</label>
        </div>
      ))}
    </ShutDownBody>
  );
}

const ShutDownBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;

  & > p {
    margin: 0 0 2px;
  }
`;

// Machine off. Plain black with quiet amber text, above everything.
const EndScreen = styled.div`
  position: fixed;
  inset: 0;
  z-index: 6000;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #d88f2a;
  font-size: 18px;
  -webkit-user-select: none;
  user-select: none;
  cursor: default;
`;

const CtxItem = styled.button`
  background: transparent;
  border: none;
  box-shadow: none;
  min-height: 0;
  min-width: 0;
  padding: 5px 12px;
  text-align: left;
  font: inherit;
  color: inherit;
  text-shadow: none;

  &&:not(:disabled):hover {
    background: #00007b;
    color: #fff;
    box-shadow: none;
  }
  &&:not(:disabled):active {
    box-shadow: none;
    text-shadow: none;
  }
  &&:focus {
    outline: none;
  }
`;

const CtxSeparator = styled.div`
  height: 2px;
  margin: 3px 5px;
  border-top: 1px solid #808080;
  border-bottom: 1px solid #fff;
`;

const SceneBlocker = styled.div`
  position: absolute;
  inset: 0;
  z-index: 4000;
  cursor: none;
`;

// ---- compact-mode pieces ----

// In-flow draggable wrapper for compact-mode windows. It keeps its slot
// in the layout but can be picked up by the title bar and moved anywhere
// — it's still a window. Pointer events cover mouse and touch alike; the
// fill variant leaves its scrolling client area to the browser.
function TouchDragBox({ style, fill = false, onActivate, children, ...delegated }) {
  const [d, setD] = React.useState({ x: 0, y: 0 });
  const sessionRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      const s = sessionRef.current;
      if (!s) return;
      window.removeEventListener("pointermove", s.onMove);
      window.removeEventListener("pointerup", s.onUp);
      window.removeEventListener("pointercancel", s.onUp);
      sessionRef.current = null;
    };
  }, []);

  const onPointerDown = (e) => {
    if (onActivate) onActivate();
    if (e.target.closest("button, input, [data-no-drag]")) return;
    if (!e.target.closest("[data-drag-handle]")) return;
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const base = { ...d };
    const onMove = (ev) => {
      setD({ x: base.x + ev.clientX - startX, y: base.y + ev.clientY - startY });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      sessionRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    sessionRef.current = { onMove, onUp };
  };

  return (
    <DragBox
      onPointerDown={onPointerDown}
      style={{ ...style, "--tx": d.x + "px", "--ty": d.y + "px" }}
      data-draggable="true"
      data-fill={fill ? "true" : undefined}
      {...delegated}
    >
      {children}
    </DragBox>
  );
}

const DragBox = styled.div`
  position: relative;
  z-index: 20;
  width: var(--w);
  height: var(--h);
  flex-shrink: 0;
  transform: translate(var(--tx), var(--ty));
  touch-action: none;
  /* iOS: a window drag must never double as a long-press selection or
     image callout on whatever the finger crossed (cards included). */
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;

  /* The Other Games wrapper fills its flex slot. Unlike the player's
     board, its client area must keep native momentum scrolling; only
     the title bar claims touch gestures for window dragging. */
  &[data-fill="true"] {
    flex: 1 1 auto;
    align-self: stretch;
    min-width: 0;
    min-height: 0;
    width: auto;
    height: auto;
    touch-action: auto;
  }

  &[data-fill="true"] [data-drag-handle] {
    touch-action: none;
  }
`;

const CompactColumn = styled.div`
  position: absolute;
  inset: 0;
  bottom: var(--taskbar-h);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 4px 4px 6px;

  &[data-landscape="true"] {
    flex-direction: row;
    align-items: stretch;
  }
`;

// Landscape: icons up top, your game pinned to the bottom-right corner.
const LandscapeRail = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

// A slice of wallpaper for the desktop icons — mobile still has a desktop.
const CompactIconStrip = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  flex-shrink: 0;
`;

const CompactBinSlot = styled.div`
  margin-left: auto;
`;

const CompactScaler = styled.div`
  width: calc(var(--w) * var(--scale));
  height: calc(var(--h) * var(--scale));
  flex-shrink: 0;
`;

// Scales fixed-size window subtrees. The transform must live on this
// plain wrapper, NOT on the window itself — SolitaireCore's open
// animation (fill-mode: both) pins `transform` on its frame and would
// silently override any transform we put there.
const ScaleBox = styled.div`
  transform: scale(var(--scale));
  transform-origin: top left;
`;

const CompactToolLayer = styled.div`
  position: absolute;
  inset: 0;
  bottom: var(--taskbar-h, 34px);
  z-index: var(--z, 500);
  /* The layer only centers the tool; it must not eat clicks meant for
     the desktop around it (a short landscape viewport looks like the
     full desktop, and a dead desktop reads as a hang). */
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;

  & > * {
    pointer-events: auto;
  }
`;

const CompactGameLayer = styled.div`
  position: absolute;
  inset: 0;
  bottom: ${TASKBAR_HEIGHT}px;
  z-index: var(--z);
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;

  & > [data-draggable] {
    pointer-events: auto;
  }
`;

export default Stage;
